import { useState, useEffect, createContext, useContext, useCallback, useMemo, lazy, Suspense } from "react";
import { AutoTranslate } from "@/components/AutoTranslate";
import { WelcomeNotice } from "@/components/welcome/WelcomeNotice";
import { hasRole } from "@/lib/roles";
import { perfText } from "@/lib/monthlyPerfText";
import { TrendingUp } from "lucide-react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Toaster } from "@/components/ui/sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Globe, HelpCircle, Smile, Puzzle, LayoutDashboard, Calculator as CalcIcon, NotebookPen, Megaphone, Sun, Moon, ShieldAlert, SlidersHorizontal, Share2, History as HistoryIcon, LogOut, Sparkles, Users as UsersIcon, ShieldCheck, FileText, GraduationCap, LifeBuoy, ScrollText, CalendarDays, Rocket, Link2 as LinkIcon, Image as ImageIcon, Video, Eye, Newspaper, Users, Network as NetworkIcon, MessageSquare, Database, BarChart3, Mail, Tag, Languages, Youtube, ChevronDown, Activity } from "lucide-react";
import { getT, LANGS, fmtEur } from "@/i18n";
import PublicGate, { readGateUnlock } from "@/components/PublicGate";
import { Sidebar } from "@/components/Sidebar";
import { MobileMenu } from "@/components/MobileMenu";
import { HeroBanner } from "@/components/HeroBanner";
import { DisclaimerGate } from "@/components/DisclaimerGate";
import { Trophy, ClipboardCheck, Lightbulb, Bot } from "lucide-react";
import { funText } from "@/components/funquiz/funQuizText";
import { localizeMenu } from "@/menuLabels";
import Tour from "@/pages/Tour";
import { BusyBanner, installBusyInterceptor } from "@/components/BusyBanner";
installBusyInterceptor();

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Journey = lazy(() => import("@/pages/Journey"));
const Strategy = lazy(() => import("@/pages/Strategy"));
const SonicAI = lazy(() => import("@/pages/SonicAI"));
const Calculator = lazy(() => import("@/pages/Calculator"));
const Tracking = lazy(() => import("@/pages/Tracking"));
const History = lazy(() => import("@/pages/History"));
const Social = lazy(() => import("@/pages/Social"));
const Settings = lazy(() => import("@/pages/Settings"));
const SiteSettings = lazy(() => import("@/pages/SiteSettings"));
const ShareHistory = lazy(() => import("@/pages/ShareHistory"));
const PublicJourneyPage = lazy(() => import("@/pages/PublicJourneyPage"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Members = lazy(() => import("@/pages/Members"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const RiskCenter = lazy(() => import("@/pages/RiskCenter"));
const AdminRiskDocs = lazy(() => import("@/pages/AdminRiskDocs"));
const Academy = lazy(() => import("@/pages/Academy"));
const Links = lazy(() => import("@/pages/Links"));
const LinksAdmin = lazy(() => import("@/pages/LinksAdmin"));
const News = lazy(() => import("@/pages/News"));
const AdminNews = lazy(() => import("@/pages/AdminNews"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const AdminGallery = lazy(() => import("@/pages/AdminGallery"));
const PublicGallery = lazy(() => import("@/pages/PublicGallery"));
const Network = lazy(() => import("@/pages/Network"));
const Messages = lazy(() => import("@/pages/Messages"));
const AdminNetwork = lazy(() => import("@/pages/AdminNetwork"));
const MarketingMaterial = lazy(() => import("@/pages/MarketingMaterial"));
const Guides = lazy(() => import("@/pages/Guides"));
const GuidesAdmin = lazy(() => import("@/pages/GuidesAdmin"));
const OnboardingWizard = lazy(() => import("@/pages/OnboardingWizard"));
const AdminOnboardingWizard = lazy(() => import("@/pages/AdminOnboardingWizard"));
const FaqAssistant = lazy(() => import("@/pages/FaqAssistant"));
const MonthlyPerformance = lazy(() => import("@/pages/MonthlyPerformance"));
const AdminFaqAssistant = lazy(() => import("@/pages/AdminFaqAssistant"));
const Stickers = lazy(() => import("@/pages/Stickers"));
const MemberCalculator = lazy(() => import("@/pages/MemberCalculator"));
const MarketingMaterialAdmin = lazy(() => import("@/pages/MarketingMaterialAdmin"));
const MarketingCategoriesAdmin = lazy(() => import("@/pages/MarketingCategoriesAdmin"));
const CourseCategoriesAdmin = lazy(() => import("@/pages/CourseCategoriesAdmin"));
const Translators = lazy(() => import("@/pages/Translators"));
const MailTemplatesAdmin = lazy(() => import("@/pages/MailTemplatesAdmin"));
const PerformanceUpload = lazy(() => import("@/pages/PerformanceUpload"));
const LandingCmsAdmin = lazy(() => import("@/pages/LandingCmsAdmin"));
const Welcome = lazy(() => import("@/pages/Welcome"));
const Feedback = lazy(() => import("@/pages/Feedback"));
const AdminAcademy = lazy(() => import("@/pages/AdminAcademy"));
const AdminMenuVisibility = lazy(() => import("@/pages/AdminMenuVisibility"));
const AdminBackup = lazy(() => import("@/pages/AdminBackup"));
const AdminImageOptimizer = lazy(() => import("@/pages/AdminImageOptimizer"));
const AdminMT5Integration = lazy(() => import("@/pages/AdminMT5Integration"));
const AdminLandingContact = lazy(() => import("@/pages/AdminLandingContact"));
const AdminUserStats = lazy(() => import("@/pages/AdminUserStats"));
const AdminHomepageBanner = lazy(() => import("@/pages/AdminHomepageBanner"));
const AdminInvitations = lazy(() => import("@/pages/AdminInvitations"));
const AdminTeams = lazy(() => import("@/pages/AdminTeams"));
const Courses = lazy(() => import("@/pages/Courses"));
const AdminCourses = lazy(() => import("@/pages/AdminCourses"));
const Support = lazy(() => import("@/pages/Support"));
const AdminSupportCategories = lazy(() => import("@/pages/AdminSupportCategories"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const LandingConfig = lazy(() => import("@/pages/LandingConfig"));
const Calendar = lazy(() => import("@/pages/Calendar"));
const PublicHome = lazy(() => import("@/pages/PublicHome"));
const PublicLanding = lazy(() => import("@/pages/PublicLanding"));
const PublicSupport = lazy(() => import("@/pages/PublicSupport"));
const PublicOnboarding = lazy(() => import("@/pages/PublicOnboarding"));
const OnboardingForms = lazy(() => import("@/pages/OnboardingForms"));
const AdminOnboardingTemplate = lazy(() => import("@/pages/AdminOnboardingTemplate"));
const PublicPrivacy = lazy(() => import("@/pages/PublicPrivacy"));
const PublicTerms = lazy(() => import("@/pages/PublicTerms"));
const Leads = lazy(() => import("@/pages/Leads"));
const PartnerRegistrations = lazy(() => import("@/pages/PartnerRegistrations"));
const SiteGateAdmin = lazy(() => import("@/pages/SiteGateAdmin"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Challenge = lazy(() => import("@/pages/Challenge"));
const AdminChallenge = lazy(() => import("@/pages/AdminChallenge"));
const FunQuiz = lazy(() => import("@/pages/FunQuiz"));
const AdminFunQuiz = lazy(() => import("@/pages/AdminFunQuiz"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const BACKEND = BACKEND_URL;
const PageLoader = () => (
  <div className="flex items-center justify-center py-24" data-testid="page-loader">
    <div className="h-6 w-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
  </div>
);

export const API = `${BACKEND_URL}/api`;

const NO_HERO_TABS = new Set(["feedback", "stickers", "welcome", "calendar", "courses", "adminCourses", "quiz", "funQuiz", "adminFunQuiz", "adminChallenge", "academy", "news", "network", "gallery", "links", "marketingMaterial", "guides", "adminGuides", "adminWizard", "faqAssistant", "adminFaqAssistant", "monthlyPerf", "myLeads", "messages", "onboardingForms", "adminOnboardingTemplate"]);

const LANG_NAMES = { de: "Deutsch", en: "English", it: "Italiano", tr: "Türkçe", ru: "Русский", es: "Español", fr: "Français", pl: "Polski", sr: "Српски", hr: "Hrvatski", sq: "Shqip", sl: "Slovenščina", pt: "Português", cs: "Čeština", hu: "Magyar", hi: "हिन्दी", ms: "Bahasa Melayu", nl: "Nederlands", zh: "简体中文" };
const SORTED_LANGS = [...LANGS].sort((a, b) => (LANG_NAMES[a] || a).localeCompare(LANG_NAMES[b] || b, "de"));

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const storedToken = localStorage.getItem("token");
if (storedToken) axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;

const RETIRED_PORTAL_TABS = new Set(["aiStudio", "adminAiStudio"]);
const normalizePortalTab = (tab) => RETIRED_PORTAL_TABS.has(tab) ? "welcome" : tab || "welcome";

function readPortalTab() {
  if (typeof window === "undefined" || window.location.pathname !== "/") return "welcome";
  const url = new URL(window.location.href);
  const requested = url.searchParams.get("tab");
  if (RETIRED_PORTAL_TABS.has(requested)) {
    url.searchParams.delete("tab");
    // Replace obsolete bookmarks/history entries, preserving unrelated URL state.
    try {
      window.history.replaceState({ ...window.history.state, tab: "welcome" }, "", url.toString());
    } catch { /* the retired page still resolves to welcome if history is unavailable */ }
  }
  return normalizePortalTab(requested);
}

function App() {
  const [lang, setLang] = useState(() => {
    // 1. Stored user preference wins
    const stored = localStorage.getItem("lang");
    if (stored && LANGS.includes(stored)) return stored;
    // 2. First visit → read browser language (navigator.language or navigator.languages)
    try {
      const cands = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ""])
        .map((x) => (x || "").toLowerCase().split("-")[0]);
      for (const c of cands) {
        if (LANGS.includes(c)) return c;
      }
    } catch {}
    // 3. Fallback
    return "de";
  });
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  const [stats, setStats] = useState(null);
  // Initial active tab comes from the URL (?tab=...) so deep-links and browser
  // back/forward keep working across every sidebar entry. Guard against non-app
  // paths like /register or /reset-password where "?tab=" is irrelevant.
  const [activeTab, _setActiveTab] = useState(readPortalTab);

  // Wrapper: whenever the active tab changes, push a new history entry so the
  // browser back-button (and mouse-side buttons) navigate between tabs instead
  // of exiting the SPA. The pushState call lives OUTSIDE the setState updater
  // so React Strict Mode's dev-only double-invoke does not create duplicate
  // history entries.
  const setActiveTab = useCallback((next) => {
    next = normalizePortalTab(next);
    if (typeof window !== "undefined") {
      try {
        const url = new URL(window.location.href);
        if (url.pathname === "/" || url.pathname === "") {
          const cur = url.searchParams.get("tab") || "welcome";
          if (cur !== next) {
            if (next === "welcome") url.searchParams.delete("tab");
            else url.searchParams.set("tab", next);
            window.history.pushState({ tab: next }, "", url.toString());
          }
        }
      } catch { /* history API disabled – degrade gracefully */ }
    }
    _setActiveTab(next);
  }, []);

  // React to browser back / forward navigation – swap `activeTab` in without
  // pushing a new entry.
  useEffect(() => {
    const onPop = () => {
      try {
        _setActiveTab(readPortalTab());
      } catch { /* noop */ }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [socialPrefill, setSocialPrefill] = useState(null);
  const [messagesPeerId, setMessagesPeerId] = useState(null);
  const [galleryEventId, setGalleryEventId] = useState(null);
  const [msgUnread, setMsgUnread] = useState(0);
  const [obUnseen, setObUnseen] = useState(0);

  // Wrapper so Welcome can request opening a specific gallery event.
  const navigateTo = useCallback((tab, payload) => {
    if (tab === "gallery" && payload?.eventId) setGalleryEventId(payload.eventId);
    if (tab === "messages" && payload?.peerId) setMessagesPeerId(payload.peerId);
    setActiveTab(tab);
  }, []);
  const [token, setTokenState] = useState(() => localStorage.getItem("token"));
  const [user, setUser] = useState(null);
  const [authRequired, setAuthRequired] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [onboarding, setOnboarding] = useState(null); // { onboarding_done, reconsent_required, pending_doc_ids, profile_set }
  const [gateEnabled, setGateEnabled] = useState(null); // null=loading, true|false
  const [gateUnlocked, setGateUnlocked] = useState(() => !!readGateUnlock());
  const [menuHidden, setMenuHidden] = useState(() => new Set());
  const [menuOrder, setMenuOrder] = useState([]);
  const [quizEnabled, setQuizEnabled] = useState(false);
  const [faqAvailable, setFaqAvailable] = useState(false);
  useEffect(() => {
    if (!token) { setFaqAvailable(false); return; }
    const load = () => axios.get(`${API}/faq-assistant/status`).then((r) => setFaqAvailable(!!r.data.available)).catch(() => setFaqAvailable(false));
    load();
    window.addEventListener("faq-assistant-changed", load);
    return () => window.removeEventListener("faq-assistant-changed", load);
  }, [token]);
  const [funQuizAvailable, setFunQuizAvailable] = useState(false);
  const [branding, setBranding] = useState({ name: "", logo_url: "", primary_color: null, theme_mode: null, login_welcome: null });
  const t = getT(lang);

  // Domain-based branding (fetched once at page load, no auth required)
  useEffect(() => {
    const host = (window.location.hostname || "").toLowerCase();
    if (!host) return;
    axios.get(`${API}/teams/branding`, { params: { domain: host } })
      .then((r) => {
        if (r.data?.team) setBranding({
          name: r.data.team.name || "",
          logo_url: r.data.team.logo_url || "",
          primary_color: r.data.team.primary_color || null,
          theme_mode: r.data.team.theme_mode || null,
          login_welcome: r.data.team.login_welcome || null,
        });
      })
      .catch(() => {});
  }, []);

  // Team theme_mode once at load — gives teams a hard preference without blocking user override.
  const [themeApplied, setThemeApplied] = useState(false);
  const setDarkUser = useCallback((v) => { localStorage.setItem("theme_user_set", "1"); setDark(v); }, []);
  useEffect(() => {
    if (themeApplied || localStorage.getItem("theme_user_set")) return;
    const mode = branding?.theme_mode;
    if (mode === "dark") { setDark(true); setThemeApplied(true); }
    else if (mode === "light") { setDark(false); setThemeApplied(true); }
  }, [branding?.theme_mode, themeApplied]);

  // Apply team primary color as CSS variable `--primary` (HSL triple used by Tailwind).
  useEffect(() => {
    const root = document.documentElement;
    const hex = branding?.primary_color;
    if (!hex) {
      root.style.removeProperty("--primary");
      return;
    }
    // Convert #RGB or #RRGGBB (ignore alpha) → "H S% L%" for Tailwind.
    let h = hex.replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    if (h.length === 8) h = h.slice(0, 6);
    if (h.length !== 6) return;
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let hh = 0, s = 0; const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
        case g: hh = (b - r) / d + 2; break;
        default: hh = (r - g) / d + 4;
      }
      hh /= 6;
    }
    root.style.setProperty("--primary", `${Math.round(hh * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`);
  }, [branding?.primary_color]);

  const setToken = useCallback((tok) => {
    if (tok) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${tok}`;
      localStorage.setItem("token", tok);
    } else {
      // Best-effort logout ping so analytics can compute session duration.
      // Fire-and-forget: do not block token clearing on network response.
      const prev = localStorage.getItem("token");
      if (prev) { try { axios.post(`${API}/auth/logout`, null, { headers: { Authorization: `Bearer ${prev}` } }).catch(() => {}); } catch { /* ignore */ } }
      delete axios.defaults.headers.common["Authorization"];
      localStorage.removeItem("token");
    }
    setTokenState(tok);
  }, []);

  useEffect(() => {
    axios.get(`${API}/auth/status`).then((r) => setAuthRequired(r.data.required)).catch(() => setAuthRequired(false));
    axios.get(`${API}/site/gate`).then((r) => setGateEnabled(!!r.data.enabled)).catch(() => setGateEnabled(false));
    const id = axios.interceptors.response.use(
      (r) => r,
      (e) => {
        // Only log out if the rejected request was sent with the *current* token.
        // Late 401s from requests sent before login (no/old token) must not wipe a fresh session.
        const sent = e.config?.headers?.Authorization || e.config?.headers?.authorization;
        const current = localStorage.getItem("token");
        if (e.response?.status === 401 && current && sent === `Bearer ${current}`) setToken(null);
        return Promise.reject(e);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`).then((r) => setUser(r.data)).catch(() => setUser(null));
    } else {
      setUser(null);
      setOnboarding(null);
    }
  }, [token]);

  const refreshOnboarding = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/onboarding/status`);
      setOnboarding(r.data);
      return r.data;
    } catch {
      setOnboarding(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (user && !user.is_owner) refreshOnboarding();
    else if (user?.is_owner) setOnboarding({ onboarding_done: true, reconsent_required: false, pending_doc_ids: [], profile_set: true });
  }, [user, refreshOnboarding]);

  // User-team branding: once the user is loaded, if they belong to a team,
  // fetch that team and overlay its logo + name + theme on top of the
  // domain-based branding. This way a Team-B member sees Team-B's logo/name
  // in the sidebar top-left even when logged in via the generic portal domain.
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/me/team`).then((r) => {
      const team = r.data?.team;
      if (!team) return;
      setBranding((prev) => ({
        ...prev,
        name: team.name || prev.name,
        logo_url: team.logo_url || prev.logo_url,
        primary_color: team.primary_color || prev.primary_color,
        theme_mode: team.theme_mode || prev.theme_mode,
        login_welcome: team.login_welcome || prev.login_welcome,
      }));
    }).catch(() => {});
  }, [user]);

  const openSocial = useCallback((prefill) => {
    setSocialPrefill(prefill);
    setActiveTab("social");
  }, []);
  const clearSocialPrefill = useCallback(() => setSocialPrefill(null), []);

  // Poll unread-count for the sidebar badge (every 45 s while logged in).
  useEffect(() => {
    if (!user) { setMsgUnread(0); return; }
    let cancel = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/messages/unread-count`);
        if (!cancel) setMsgUnread(r.data?.unread || 0);
        const o = await axios.get(`${API}/onboarding-forms/unseen`);
        if (!cancel) setObUnseen(o.data?.unseen || 0);
      } catch { /* ignore */ }
    };
    load();
    return () => { cancel = true; };
  }, [user, activeTab]);

  // Single background pulse: unread badge + presence (every authed request touches last_seen).
  // Paused while the tab is hidden or the user has been idle for 5 min; resumes instantly on return.
  useEffect(() => {
    if (!user) return;
    const PULSE_MS = 60 * 1000;
    const IDLE_MS = 5 * 60 * 1000;
    let lastActivity = Date.now();
    let lastPulse = Date.now();
    const pulse = () => {
      lastPulse = Date.now();
      axios.get(`${API}/messages/unread-count`)
        .then((r) => setMsgUnread(r.data?.unread || 0))
        .catch(() => { /* 401 handled by interceptor */ });
      axios.get(`${API}/onboarding-forms/unseen`)
        .then((r) => setObUnseen(r.data?.unseen || 0))
        .catch(() => {});
    };
    const markActive = () => {
      const wasIdle = Date.now() - lastActivity > IDLE_MS;
      lastActivity = Date.now();
      if (wasIdle && Date.now() - lastPulse > PULSE_MS) pulse();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - lastPulse > PULSE_MS / 2) { lastActivity = Date.now(); pulse(); }
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];
    events.forEach((ev) => window.addEventListener(ev, markActive, { passive: true }));
    document.addEventListener("visibilitychange", onVisible);
    const t = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastActivity > IDLE_MS) return;
      pulse();
    }, PULSE_MS);
    return () => {
      clearInterval(t);
      events.forEach((ev) => window.removeEventListener(ev, markActive));
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    localStorage.setItem("lang", lang);
  }, [lang]);

  const sidebarItems = useMemo(() => localizeMenu([
    { id: "welcome", label: t.tabs.welcome || "Willkommen", icon: Rocket },
    { id: "calendar", label: t.tabs.calendar, icon: CalendarDays },
    {
      id: "__marketing", label: t.tabs.marketing || "Marketing", icon: Megaphone,
      children: [
        { id: "strategy", label: t.tabs.strategy, icon: Sparkles },
        { id: "marketingMaterial", label: t.tabs.marketingMaterial || "Marketingmaterial", icon: ImageIcon },
        { id: "monthlyPerf", label: perfText(lang).title, icon: TrendingUp },
        { id: "guides", label: "Anleitungen to go", icon: FileText },
        { id: "myLeads", label: "Partner - Registrierungen", icon: UsersIcon },
        { id: "memberCalculator", label: "Rechner", icon: CalcIcon },
        { id: "stickers", label: "Sticker", icon: Smile },
        { id: "onboardingForms", label: "Onboarding Formular", icon: ClipboardCheck, badge: obUnseen },
      ],
    },
    { id: "academy", label: t.tabs.academy, icon: GraduationCap },
    ...(faqAvailable ? [{ id: "faqAssistant", label: "FAQ Assistent", icon: Bot }] : []),
    ...(quizEnabled ? [{ id: "quiz", label: "Sonic Quiz Challenge", icon: Trophy }] : []),
    ...(funQuizAvailable ? [{ id: "funQuiz", label: funText(lang).title, icon: Puzzle }] : []),
    { id: "courses", label: t.tabs.courses || "Videokurse", icon: Video },
    {
      id: "dashboard", label: t.tabs.dashboard, icon: LayoutDashboard, selectable: true,
      children: [
        { id: "tracking", label: t.tabs.tracking, icon: NotebookPen },
        { id: "social", label: t.tabs.social, icon: Megaphone },
      ],
    },
    {
      id: "__profitCalc", label: t.tabs.profitCalc || "Profitrechner", icon: CalcIcon,
      children: [
        { id: "history", label: t.tabs.history, icon: HistoryIcon },
        { id: "calculator", label: t.tabs.calculator, icon: CalcIcon },
      ],
    },
    { id: "links", label: t.tabs.links || "Links", icon: LinkIcon },
    { id: "news", label: t.tabs.news || "News", icon: Newspaper },
    { id: "gallery", label: t.tabs.gallery || "Galerie", icon: ImageIcon },
    { id: "network", label: t.tabs.network || "Mein Team", icon: Users },
    { id: "messages", label: t.tabs.messages || "Nachrichten", icon: MessageSquare, badge: msgUnread },
    ...((hasRole(user, "admin", "translator")) ? [
      { id: "translators", label: "Übersetzer", icon: Languages },
    ] : []),
    ...(hasRole(user, "moderator") && !hasRole(user, "admin") ? [
      { id: "adminAcademy", label: t.tabs.adminAcademy, icon: GraduationCap },
      { id: "adminCourses", label: t.tabs.adminCourses || "Kurse (Admin)", icon: Video },
      { id: "adminLinks", label: (t.tabs.adminLinks || "Links (Admin)"), icon: LinkIcon },
      { id: "adminNews", label: "News (Admin)", icon: Newspaper },
      { id: "adminGallery", label: "Galerie (Admin)", icon: ImageIcon },
      { id: "adminMarketingMaterial", label: (t.tabs.adminMarketingMaterial || "Material (Admin)"), icon: ImageIcon },
      { id: "adminGuides", label: "Anleitungen (Admin)", icon: FileText },
      { id: "leads", label: t.tabs.leads || "Leads", icon: UsersIcon },
    ] : []),
    ...(hasRole(user, "admin") ? [{
      id: "__admin", label: t.tabs.adminGroup || "Admin", icon: ShieldAlert,
      children: [
        {
          id: "__admin_quiz", label: "Knowledge Challenge", icon: Trophy,
          children: [
            { id: "adminChallenge", label: "Quiz Challenge", icon: Trophy },
            { id: "adminFunQuiz", label: "Spaß-Quiz", icon: Puzzle },
          ],
        },
        {
          id: "__admin_content", label: "Inhalte", icon: FileText,
          children: [
            { id: "adminNews", label: "News (Admin)", icon: Newspaper },
            { id: "adminGallery", label: "Galerie (Admin)", icon: ImageIcon },
            { id: "adminAcademy", label: t.tabs.adminAcademy, icon: GraduationCap },
            { id: "adminCourses", label: t.tabs.adminCourses || "Kurse (Admin)", icon: Video },
            { id: "adminCourseCategories", label: "Kurs-Kategorien", icon: Video },
            { id: "adminMarketingMaterial", label: (t.tabs.adminMarketingMaterial || "Material (Admin)"), icon: ImageIcon },
            { id: "adminMarketingCategories", label: (t.tabs.adminMarketingCategories || "Marketing-Kategorien"), icon: ImageIcon },
            { id: "adminGuides", label: "Anleitungen", icon: FileText },
            { id: "adminWizard", label: "Onboarding-Assistent", icon: FileText },
            { id: "adminFaqAssistant", label: "FAQ Assistent", icon: Bot },
            { id: "adminPerformance", label: "Performance Upload", icon: BarChart3 },
            { id: "adminLandingCms", label: "Landing · FAQ", icon: HelpCircle },
            { id: "adminLandingContact", label: "Landing · Kontakt-Hero", icon: Youtube },
            { id: "adminLinks", label: (t.tabs.adminLinks || "Links (Admin)"), icon: LinkIcon },
            { id: "homepageBanner", label: "Homepage-Banner", icon: ImageIcon },
            { id: "adminOnboardingTemplate", label: "Onboarding Formular", icon: ClipboardCheck },
          ],
        },
        {
          id: "__admin_people", label: "Mitglieder", icon: UsersIcon,
          children: [
            { id: "members", label: t.tabs.members, icon: UsersIcon },
            { id: "adminInvitations", label: "Einladungen", icon: Mail },
            { id: "adminMailTemplates", label: "E-Mail-Vorlagen", icon: Mail },
            { id: "leads", label: t.tabs.leads || "Leads", icon: UsersIcon },
            { id: "adminNetwork", label: "Netzwerk (Admin)", icon: NetworkIcon },
            { id: "teams", label: "Teams", icon: UsersIcon },
          ],
        },
        {
          id: "__admin_support", label: "Support & Risiko", icon: LifeBuoy,
          children: [
            { id: "adminSupport", label: t.tabs.adminSupport || t.tabs.support, icon: LifeBuoy },
            { id: "adminSupportCategories", label: "Support-Kategorien", icon: Tag },
            { id: "riskDocs", label: t.tabs.riskDocs, icon: FileText },
            { id: "riskCenter", label: t.tabs.riskCenter, icon: ShieldCheck },
          ],
        },
        {
          id: "__admin_system", label: "System", icon: SlidersHorizontal,
          children: [
            { id: "siteSettings", label: "Seiten-Einstellungen", icon: SlidersHorizontal },
            { id: "menuVisibility", label: "Menü-Sichtbarkeit", icon: Eye },
            { id: "siteGate", label: "Coming-Soon", icon: ShieldAlert },
            { id: "adminBackup", label: "Backup", icon: Database },
            { id: "adminImageOptimizer", label: "Bild-Optimierer", icon: ImageIcon },
            { id: "adminMt5", label: "MetaTrader 5", icon: Activity },
            { id: "audit", label: t.tabs.audit, icon: ScrollText },
          ],
        },
        {
          id: "__admin_stats", label: "Statistik", icon: BarChart3,
          children: [
            { id: "userStats", label: "User-Statistik", icon: BarChart3 },
          ],
        },
      ],
    }] : []),
    ...(!hasRole(user, "admin") ? [{
      id: "settings", label: t.tabs.settings, icon: SlidersHorizontal, selectable: true,
      children: [
        { id: "riskCenter", label: t.tabs.riskCenter, icon: ShieldCheck },
      ],
    }] : []),
    { id: "feedback", label: "Feedback", icon: Lightbulb },
    { id: "support", label: "Portal Support", icon: LifeBuoy },
  ], lang), [t, lang, user?.role, user?.roles, msgUnread, obUnseen, quizEnabled, funQuizAvailable, faqAvailable]);

  useEffect(() => {
    if (!token) { setQuizEnabled(false); return; }
    let mounted = true;
    const refreshQuiz = () => {
      axios.get(`${API}/challenges/available`)
        .then((r) => { if (mounted) setQuizEnabled(r.data.available === true); })
        .catch(() => { if (mounted) setQuizEnabled(false); });
      axios.get(`${API}/fun-quiz/available`)
        .then((r) => { if (mounted) setFunQuizAvailable(r.data.available === true); })
        .catch(() => { if (mounted) setFunQuizAvailable(false); });
    };
    refreshQuiz();
    window.addEventListener("focus", refreshQuiz);
    window.addEventListener("quiz-status-changed", refreshQuiz);
    return () => { mounted = false; window.removeEventListener("focus", refreshQuiz); window.removeEventListener("quiz-status-changed", refreshQuiz); };
  }, [token, activeTab]);

  // Build a flat lookup { id → label } from all sidebar items (including children).
  // Used as fallback for the HeroBanner title when a tab has no i18n key.
  const labelById = useMemo(() => {
    const map = {};
    const walk = (items) => {
      for (const it of items || []) {
        if (it.id && it.label) map[it.id] = it.label;
        if (it.children) walk(it.children);
      }
    };
    walk(sidebarItems);
    return map;
  }, [sidebarItems]);

  // Filter sidebar items using the admin-controlled hidden list.
  // Admin/settings groups themselves stay visible so admins never lock themselves out.
  // Additionally: honour the admin-defined menu order for top-level items and children.
  const visibleSidebarItems = useMemo(() => {
    const orderIdx = new Map((menuOrder || []).map((id, i) => [id, i]));
    const sortByOrder = (arr) => {
      const decorated = arr.map((it, i) => ({ it, key: orderIdx.has(it.id) ? orderIdx.get(it.id) : 9999 + i }));
      decorated.sort((a, b) => a.key - b.key);
      return decorated.map((d) => d.it);
    };
    const filterChildren = (children) => (children || []).filter((c) => !menuHidden.has(c.id));
    const filtered = sidebarItems
      .filter((it) => it.id === "__admin" || !menuHidden.has(it.id))
      .map((it) => {
        if (!it.children) return it;
        // Admin group: keep as-is (never reorder admin panel)
        if (it.id === "__admin") return it;
        const visibleChildren = filterChildren(it.children);
        if (visibleChildren.length === 0) return null;
        return { ...it, children: sortByOrder(visibleChildren) };
      })
      .filter(Boolean);
    // Reorder top-level (leaving the admin group at its natural position)
    const sorted = sortByOrder(filtered);
    const tail = ["feedback", "support"];
    return [...sorted.filter((it) => !tail.includes(it.id)), ...tail.flatMap((id) => sorted.filter((it) => it.id === id))];
  }, [sidebarItems, menuHidden, menuOrder]);

  const refreshStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard`);
      setStats(res.data);
      return res.data;
    } catch (e) {
      console.error("dashboard fetch failed", e);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshStats();
  }, [refreshStats, token]);

  useEffect(() => {
    if (!token) { setMenuHidden(new Set()); setMenuOrder([]); return; }
    axios.get(`${API}/menu-visibility`)
      .then((r) => {
        setMenuHidden(new Set(r.data.hidden || []));
        setMenuOrder(Array.isArray(r.data.order) ? r.data.order : []);
      })
      .catch(() => { setMenuHidden(new Set()); setMenuOrder([]); });
  }, [token]);

  // Global gate: if enabled AND visitor has no auth token AND hasn't unlocked, block the entire
  // public app (including DisclaimerGate) and show the password screen instead. Applies to any URL.
  const gateShouldBlock = gateEnabled === true && !token && !gateUnlocked;

  // The public /tour marketing page must always be reachable — bypass both the
  // coming-soon gate and the auth/DisclaimerGate wrapper below.
  // Public partner onboarding checklist (no login, no gate).
  if (typeof window !== "undefined" && /^\/o\/[A-Za-z0-9_-]{8,}(\/|$)/.test(window.location.pathname)) {
    return (
      <BrowserRouter>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/o/:token" element={<PublicOnboarding />} />
          </Routes>
        </Suspense>
        <Toaster />
      </BrowserRouter>
    );
  }

  if (typeof window !== "undefined" && /^\/start\/[A-Za-z0-9-]{6,64}\/?$/.test(window.location.pathname)) {
    return (
      <BrowserRouter>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/start/:ref" element={<OnboardingWizard />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  const isPublicTour = typeof window !== "undefined" && /^\/tour(\/|$)/.test(window.location.pathname);
  if (isPublicTour) {
    return (
      <AppContext.Provider value={{ lang, setLang, t, stats, refreshStats, openSocial, socialPrefill, clearSocialPrefill, dark, setDark: setDarkUser, setToken, setShowLogin, user, setUser, onboarding, refreshOnboarding, branding }}>
        <BrowserRouter>
          <Routes>
            <Route path="/tour" element={<Tour />} />
            <Route path="*" element={<Navigate to="/tour" replace />} />
          </Routes>
        </BrowserRouter>
      </AppContext.Provider>
    );
  }

  // The new "Luxury Cyber-Gold" public landing page.
  // Reachable only via a member share-link `/l/<slug>` (Marketing-Share) OR
  // the admin-only `/landing` preview. The main domain `/` keeps the original
  // Login behaviour.
  const _path = typeof window !== "undefined" ? window.location.pathname : "/";
  const isPublicLanding =
    /^\/landing(\/|$)/.test(_path) ||
    /^\/l\/[A-Za-z0-9][A-Za-z0-9-]{1,29}(\/|$)/.test(_path);
  if (isPublicLanding) {
    return (
      <AppContext.Provider value={{ lang, setLang, t, stats, refreshStats, openSocial, socialPrefill, clearSocialPrefill, dark, setDark: setDarkUser, setToken, setShowLogin, user, setUser, onboarding, refreshOnboarding, branding }}>
        <BrowserRouter>
          <Routes>
            <Route path="/landing" element={<PublicLanding />} />
            <Route path="/l/:slug" element={<PublicLanding />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <BusyBanner lang={localStorage.getItem("landing_lang") || (navigator.language || "de").slice(0, 2).toLowerCase()} />
        </BrowserRouter>
      </AppContext.Provider>
    );
  }

  // Dedicated public /login page — reachable without disclaimer gate.
  // Once logged in, leave /login synchronously: a router <Navigate> here would ping-pong
  // between "/" and "/login" (the catch-all redirects back) until App re-renders.
  if (token && /^\/login(\/|$)/.test(_path)) window.history.replaceState(null, "", "/");
  const isPublicLogin = !token && /^\/login(\/|$)/.test(_path);
  if (isPublicLogin) {
    return (
      <AppContext.Provider value={{ lang, setLang, t, stats, refreshStats, openSocial, socialPrefill, clearSocialPrefill, dark, setDark: setDarkUser, setToken, setShowLogin, user, setUser, onboarding, refreshOnboarding, branding }}>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
      </AppContext.Provider>
    );
  }

  // Password-reset page — reachable from email links without login/gate.
  const isResetPassword = /^\/reset-password(\/|$)/.test(_path);
  if (isResetPassword) {
    return (
      <AppContext.Provider value={{ lang, setLang, t, stats, refreshStats, openSocial, socialPrefill, clearSocialPrefill, dark, setDark: setDarkUser, setToken, setShowLogin, user, setUser, onboarding, refreshOnboarding, branding }}>
        <BrowserRouter>
          <Routes>
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<Navigate to="/reset-password" replace />} />
          </Routes>
        </BrowserRouter>
      </AppContext.Provider>
    );
  }

  // Public legal/support pages required for the App Store listing. Same bypass
  // strategy as /tour — reachable without auth and without the gate.
  const publicLegalPath = typeof window !== "undefined"
    ? (/^\/support(\/|$)/.test(window.location.pathname) ? "/support"
      : /^\/privacy(\/|$)/.test(window.location.pathname) ? "/privacy"
      : /^\/terms(\/|$)/.test(window.location.pathname) ? "/terms"
      : null)
    : null;
  if (publicLegalPath) {
    return (
      <AppContext.Provider value={{ lang, setLang, t, stats, refreshStats, openSocial, socialPrefill, clearSocialPrefill, dark, setDark: setDarkUser, setToken, setShowLogin, user, setUser, onboarding, refreshOnboarding, branding }}>
        <BrowserRouter>
          <Routes>
            <Route path="/support" element={<PublicSupport />} />
            <Route path="/privacy" element={<PublicPrivacy />} />
            <Route path="/terms" element={<PublicTerms />} />
            <Route path="*" element={<Navigate to={publicLegalPath} replace />} />
          </Routes>
        </BrowserRouter>
      </AppContext.Provider>
    );
  }

  return (
    <AppContext.Provider value={{ lang, setLang, t, stats, refreshStats, openSocial, socialPrefill, clearSocialPrefill, dark, setDark: setDarkUser, setToken, setShowLogin, user, setUser, onboarding, refreshOnboarding, branding }}>
      {gateShouldBlock ? (
        <PublicGate onUnlocked={() => setGateUnlocked(true)} />
      ) : (
      <BrowserRouter key="portal">
        <DisclaimerGate>
        <div className="App min-h-screen bg-background text-foreground">

        <Routes>
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/share" element={<Navigate to="/" replace />} />
          <Route path="/share/history" element={<ShareHistory />} />
          <Route path="/strategie" element={<Strategy />} />
          <Route path="/strategie/:slug" element={<Strategy />} />
          <Route path="/reise/:slug" element={<PublicJourneyPage />} />
          <Route path="/g/:slug" element={<PublicGallery />} />
          <Route path="/sonic-ai" element={<SonicAI />} />
          <Route path="/:slug" element={<Strategy />} />
          <Route path="*" element={
            authRequired && !token ? <Login />
            : <>
        <div className="lg:flex lg:items-start">
        <Sidebar
          items={visibleSidebarItems}
          active={activeTab}
          onSelect={(id) => setActiveTab(id)}
          branding={branding}
          user={user}
          onLogout={() => setToken(null)}
          onOpenProfile={() => setActiveTab("settings")}
        />
        <div className="flex-1 min-w-0">
        <header className="w-full mx-auto px-4 sm:px-6 pt-4 sm:pt-8 pb-4 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
          <div className="min-w-0 flex-1 lg:flex-none">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-xl bg-white border border-border flex items-center justify-center p-1.5 shadow-sm shrink-0 lg:hidden">
                <img src="/logo.png" alt="Team Phoenix" className="h-full w-full object-contain" />
              </div>
              <h1 className="text-lg sm:text-2xl font-bold tracking-tight lg:hidden truncate">{t.appTitle}</h1>
            </div>
          </div>
          {activeTab === "welcome" && (
            <div className="order-last w-full lg:order-none lg:w-auto lg:flex-1 flex flex-row items-center justify-center gap-1.5 sm:gap-3 min-w-0" data-testid="header-banners">
              <WelcomeNotice lang={lang} onNavigate={setActiveTab} className="lg:max-w-3xl" />
            </div>
          )}
          <div className="flex items-center gap-2">
            {/* Compact lang dropdown on mobile – full pill row from lg up */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="lang-dropdown-btn"
                  className="h-11 sm:h-9 px-3 inline-flex items-center gap-1.5 rounded-full border border-border text-xs font-semibold uppercase text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Choose language"
                >
                  <Globe className="h-3.5 w-3.5" />
                  {lang}
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto" data-testid="lang-dropdown-menu">
                {SORTED_LANGS.map((l) => (
                  <DropdownMenuItem
                    key={l}
                    data-testid={`lang-${l}-item`}
                    onClick={() => setLang(l)}
                    className={`text-sm gap-3 ${lang === l ? "bg-primary/10 text-primary font-semibold" : ""}`}
                  >
                    <span className="w-6 text-[11px] font-mono uppercase text-muted-foreground">{l}</span>
                    {LANG_NAMES[l] || l.toUpperCase()}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              data-testid="theme-toggle-btn"
              onClick={() => setDarkUser(!dark)}
              className="h-11 w-11 sm:h-9 sm:w-9 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground transition-colors duration-200"
              aria-label="Toggle theme"
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            {authRequired && token && (
              <button
                data-testid="logout-btn"
                onClick={() => setToken(null)}
                title={t.auth.logout}
                className="h-11 w-11 sm:h-9 sm:w-9 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-destructive transition-colors duration-200"
                aria-label="Logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
            )}
          </div>
        </header>

        <main id="main-content" tabIndex={-1} className="w-full mx-auto px-4 sm:px-6 pb-16">
          <MobileMenu items={visibleSidebarItems} active={activeTab} onSelect={setActiveTab} />
          {!NO_HERO_TABS.has(activeTab) && !activeTab.startsWith("quiz-admin-") && (
            <HeroBanner
              title={t.tabs[activeTab] || labelById[activeTab] || t.tabs.dashboard}
              subtitle={t.heroSubtitles?.[activeTab]}
              testId={`hero-${activeTab}`}
            />
          )}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="hidden" data-testid="main-tabs">
              <TabsTrigger value="welcome" data-testid="tab-welcome">Welcome</TabsTrigger>
              <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
              <TabsTrigger value="calculator" data-testid="tab-calculator">Calc</TabsTrigger>
              <TabsTrigger value="tracking" data-testid="tab-tracking">Tracking</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
              <TabsTrigger value="calendar" data-testid="tab-calendar">Calendar</TabsTrigger>
              <TabsTrigger value="strategy" data-testid="tab-strategy">Strategy</TabsTrigger>
              <TabsTrigger value="riskCenter" data-testid="tab-riskCenter">Risk</TabsTrigger>
              <TabsTrigger value="academy" data-testid="tab-academy">Academy</TabsTrigger>
              <TabsTrigger value="support" data-testid="tab-support">Support</TabsTrigger>
              <TabsTrigger value="social" data-testid="tab-social">Social</TabsTrigger>
              <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
              <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
              <TabsTrigger value="siteGate" data-testid="tab-siteGate">Site Gate</TabsTrigger>
            </TabsList>
            <Suspense fallback={<PageLoader />}>
            <TabsContent value="welcome" className="fade-in"><Welcome onNavigate={navigateTo} hiddenIds={menuHidden} /></TabsContent>
            <TabsContent value="dashboard" className="fade-in"><Journey /></TabsContent>
            <TabsContent value="calculator" className="fade-in"><Calculator /></TabsContent>
            <TabsContent value="tracking" className="fade-in"><Tracking /></TabsContent>
            <TabsContent value="history" className="fade-in"><History /></TabsContent>
            <TabsContent value="calendar" className="fade-in"><Calendar /></TabsContent>
            <TabsContent value="strategy" className="fade-in"><LandingConfig /></TabsContent>
            <TabsContent value="riskCenter" className="fade-in"><RiskCenter /></TabsContent>
            <TabsContent value="academy" className="fade-in"><Academy /></TabsContent>
            <TabsContent value="quiz" className="fade-in">{activeTab === "quiz" && <Challenge />}</TabsContent>
            <TabsContent value="funQuiz" className="fade-in"><FunQuiz /></TabsContent>
            {hasRole(user, "admin") && <TabsContent value="adminFunQuiz" className="fade-in"><AdminFunQuiz /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="adminChallenge" className="fade-in">{activeTab === "adminChallenge" && <AdminChallenge />}</TabsContent>}
            <TabsContent value="courses" className="fade-in"><Courses /></TabsContent>
            <TabsContent value="links" className="fade-in"><Links /></TabsContent>
            <TabsContent value="news" className="fade-in"><News /></TabsContent>
            <TabsContent value="gallery" className="fade-in"><Gallery initialEventId={galleryEventId} /></TabsContent>
            <TabsContent value="network" className="fade-in"><Network onOpenChat={(id) => { setMessagesPeerId(id); setActiveTab("messages"); }} /></TabsContent>
            <TabsContent value="messages" className="fade-in"><AutoTranslate><Messages initialPeerId={messagesPeerId} /></AutoTranslate></TabsContent>
            <TabsContent value="marketingMaterial" className="fade-in"><MarketingMaterial /></TabsContent>
            <TabsContent value="guides" className="fade-in"><Guides /></TabsContent>
            <TabsContent value="myLeads" className="fade-in">{activeTab === "myLeads" && <PartnerRegistrations />}</TabsContent>
            <TabsContent value="onboardingForms" className="fade-in">{activeTab === "onboardingForms" && <AutoTranslate><OnboardingForms onSeen={() => axios.get(`${API}/onboarding-forms/unseen`).then((r) => setObUnseen(r.data?.unseen || 0)).catch(() => {})} /></AutoTranslate>}</TabsContent>
            <TabsContent value="memberCalculator" className="fade-in"><MemberCalculator /></TabsContent>
            <TabsContent value="stickers" className="fade-in"><AutoTranslate><Stickers /></AutoTranslate></TabsContent>
            {(hasRole(user, "admin", "translator")) && (
              <TabsContent value="translators" className="fade-in"><Translators user={user} /></TabsContent>
            )}
            <TabsContent value="support" className="fade-in"><Support /></TabsContent>
            <TabsContent value="feedback" className="fade-in">{activeTab === "feedback" && <Feedback onNavigate={navigateTo} />}</TabsContent>
            <TabsContent value="social" className="fade-in"><Social /></TabsContent>
            {hasRole(user, "admin") && <TabsContent value="members" className="fade-in"><Members /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="adminInvitations" className="fade-in"><AdminInvitations onNavigate={setActiveTab} /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="adminMailTemplates" className="fade-in"><MailTemplatesAdmin /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="adminPerformance" className="fade-in"><PerformanceUpload /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="adminLandingCms" className="fade-in"><LandingCmsAdmin /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="adminLandingContact" className="fade-in"><AdminLandingContact /></TabsContent>}
            {(hasRole(user, "admin", "moderator")) && <TabsContent value="leads" className="fade-in"><Leads /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="riskDocs" className="fade-in"><AdminRiskDocs /></TabsContent>}
            {(hasRole(user, "admin", "moderator")) && <TabsContent value="adminAcademy" className="fade-in"><AdminAcademy /></TabsContent>}
            {(hasRole(user, "admin", "moderator")) && <TabsContent value="adminCourses" className="fade-in"><AdminCourses /></TabsContent>}
            {(hasRole(user, "admin", "moderator")) && <TabsContent value="adminLinks" className="fade-in"><LinksAdmin /></TabsContent>}
            {(hasRole(user, "admin", "moderator")) && <TabsContent value="adminNews" className="fade-in"><AdminNews /></TabsContent>}
            {(hasRole(user, "admin", "moderator")) && <TabsContent value="adminGallery" className="fade-in"><AdminGallery /></TabsContent>}
            {(hasRole(user, "admin", "moderator")) && <TabsContent value="adminNetwork" className="fade-in"><AdminNetwork /></TabsContent>}
            {(hasRole(user, "admin", "moderator")) && <TabsContent value="adminMarketingMaterial" className="fade-in"><MarketingMaterialAdmin /></TabsContent>}
            {(hasRole(user, "admin", "moderator")) && <TabsContent value="adminMarketingCategories" className="fade-in"><MarketingCategoriesAdmin /></TabsContent>}
            {(hasRole(user, "admin", "moderator")) && <TabsContent value="adminGuides" className="fade-in"><GuidesAdmin /></TabsContent>}
            {faqAvailable && <TabsContent value="faqAssistant" className="fade-in">{activeTab === "faqAssistant" && <FaqAssistant />}</TabsContent>}
            <TabsContent value="monthlyPerf" className="fade-in">{activeTab === "monthlyPerf" && <MonthlyPerformance />}</TabsContent>
            {hasRole(user, "admin") && <TabsContent value="adminFaqAssistant" className="fade-in">{activeTab === "adminFaqAssistant" && <AdminFaqAssistant />}</TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="adminWizard" className="fade-in">{activeTab === "adminWizard" && <AdminOnboardingWizard />}</TabsContent>}
            {(hasRole(user, "admin", "moderator")) && <TabsContent value="adminCourseCategories" className="fade-in"><CourseCategoriesAdmin /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="adminSupport" className="fade-in"><Support adminMode /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="adminSupportCategories" className="fade-in"><AdminSupportCategories /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="audit" className="fade-in"><AuditLog /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="siteGate" className="fade-in"><SiteGateAdmin /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="menuVisibility" className="fade-in"><AdminMenuVisibility /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="adminBackup" className="fade-in"><AdminBackup /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="adminImageOptimizer" className="fade-in"><AdminImageOptimizer /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="adminMt5" className="fade-in"><AdminMT5Integration /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="userStats" className="fade-in"><AdminUserStats /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="homepageBanner" className="fade-in"><AdminHomepageBanner /></TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="adminOnboardingTemplate" className="fade-in">{activeTab === "adminOnboardingTemplate" && <AdminOnboardingTemplate />}</TabsContent>}
            {hasRole(user, "admin") && <TabsContent value="teams" className="fade-in"><AdminTeams /></TabsContent>}
            <TabsContent value="settings" className="fade-in"><Settings /></TabsContent>
            <TabsContent value="siteSettings" className="fade-in"><SiteSettings /></TabsContent>
            </Suspense>
          </Tabs>
        </main>
        </div>
        </div>
          </>
          } />
        </Routes>

        <Toaster position="top-right" richColors />
        {user && <BusyBanner />}
        </div>
        </DisclaimerGate>
      </BrowserRouter>
      )}
    </AppContext.Provider>
  );
}

export default App;
